/**
 * Analyzes If/Else branches for potential conflicts/overlaps
 * Returns warnings for user review
 */

/**
 * Main function to detect conflicts between workflow branches
 * @param {Array} branches - Array of branch objects with conditionGroups
 * @returns {Array} Array of warning objects
 */
export const detectBranchConflicts = (branches) => {
  const warnings = [];
  
  if (!branches || branches.length < 2) {
    return warnings;
  }
  
  // Group conditions by field for analysis
  const conditionsByField = {};
  
  branches.forEach((branch, branchIndex) => {
    branch.conditionGroups?.forEach(group => {
      group.conditions?.forEach(condition => {
        const field = condition.field;
        if (!field) return;
        
        if (!conditionsByField[field]) {
          conditionsByField[field] = [];
        }
        conditionsByField[field].push({
          branchIndex,
          branchName: branch.name || `Branch ${branchIndex + 1}`,
          condition
        });
      });
    });
  });
  
  // Check each field for conflicts
  Object.entries(conditionsByField).forEach(([field, conditions]) => {
    if (conditions.length < 2) return;
    
    // Check for exact match conflicts (same value in multiple branches with 'equals')
    const equalsConditions = conditions.filter(c => c.condition.operator === 'equals');
    const valueMap = {};
    equalsConditions.forEach(c => {
      const val = String(c.condition.value || '').toLowerCase();
      if (val && valueMap[val]) {
        warnings.push({
          type: 'duplicate',
          severity: 'error',
          message: `Duplicate condition: "${getFieldLabel(field)}" equals "${c.condition.value}" appears in both "${valueMap[val]}" and "${c.branchName}"`,
          branches: [valueMap[val], c.branchName],
          field
        });
      } else if (val) {
        valueMap[val] = c.branchName;
      }
    });
    
    // Check for numeric range overlaps
    const numericConditions = conditions.filter(c => 
      ['greater_than', 'greater_than_or_equal', 'less_than', 
       'less_than_or_equal', 'between', 'not_between', 'equals'].includes(c.condition.operator) &&
      isNumericValue(c.condition.value) || 
      (c.condition.operator === 'between' && isNumericValue(c.condition.value_min))
    );
    
    if (numericConditions.length >= 2) {
      const overlaps = findNumericOverlaps(numericConditions);
      warnings.push(...overlaps);
    }
    
    // Check for contradictory conditions (e.g., equals and not_equals same value in different branches)
    const contradictions = findContradictions(conditions);
    warnings.push(...contradictions);
  });
  
  return warnings;
};

/**
 * Get a human-readable label for a field
 */
const getFieldLabel = (field) => {
  if (field.startsWith('custom_field.')) {
    return field.replace('custom_field.', '');
  }
  return field.split('.').pop() || field;
};

/**
 * Check if a value is numeric
 */
const isNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Find contradictory conditions between branches
 */
const findContradictions = (conditions) => {
  const warnings = [];
  
  // Group by branch
  const byBranch = {};
  conditions.forEach(c => {
    if (!byBranch[c.branchName]) {
      byBranch[c.branchName] = [];
    }
    byBranch[c.branchName].push(c);
  });
  
  // Check for always-false scenarios
  const branchNames = Object.keys(byBranch);
  for (let i = 0; i < branchNames.length; i++) {
    for (let j = i + 1; j < branchNames.length; j++) {
      const branch1Conditions = byBranch[branchNames[i]];
      const branch2Conditions = byBranch[branchNames[j]];
      
      branch1Conditions.forEach(c1 => {
        branch2Conditions.forEach(c2 => {
          // Check if both use equals with same value
          if (c1.condition.operator === 'equals' && 
              c2.condition.operator === 'equals' &&
              String(c1.condition.value).toLowerCase() === String(c2.condition.value).toLowerCase()) {
            // This is a duplicate - already handled above
            return;
          }
        });
      });
    }
  }
  
  return warnings;
};

/**
 * Find numeric range overlaps between conditions
 */
const findNumericOverlaps = (conditions) => {
  const warnings = [];
  const ranges = conditions.map(c => ({
    ...parseNumericRange(c.condition),
    branchName: c.branchName,
    condition: c.condition
  }));
  
  // Compare each pair of ranges
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      // Skip if same branch
      if (ranges[i].branchName === ranges[j].branchName) continue;
      
      if (rangesOverlap(ranges[i].range, ranges[j].range)) {
        warnings.push({
          type: 'overlap',
          severity: 'warning',
          message: `Potential overlap: "${ranges[i].branchName}" (${ranges[i].description}) overlaps with "${ranges[j].branchName}" (${ranges[j].description})`,
          branches: [ranges[i].branchName, ranges[j].branchName],
          suggestion: 'Consider using exclusive ranges like 0-100 and 101-200 instead of 0-100 and 100-200'
        });
      }
    }
  }
  
  return warnings;
};

/**
 * Parse a condition into a numeric range
 */
const parseNumericRange = (condition) => {
  const { operator, value, value_min, value_max } = condition;
  const epsilon = 0.0001; // Small value for exclusive comparisons
  
  switch (operator) {
    case 'equals':
      return { 
        range: { min: parseFloat(value), max: parseFloat(value) },
        description: `= ${value}`
      };
    case 'greater_than':
      return { 
        range: { min: parseFloat(value) + epsilon, max: Infinity },
        description: `> ${value}`
      };
    case 'greater_than_or_equal':
      return { 
        range: { min: parseFloat(value), max: Infinity },
        description: `≥ ${value}`
      };
    case 'less_than':
      return { 
        range: { min: -Infinity, max: parseFloat(value) - epsilon },
        description: `< ${value}`
      };
    case 'less_than_or_equal':
      return { 
        range: { min: -Infinity, max: parseFloat(value) },
        description: `≤ ${value}`
      };
    case 'between':
      return { 
        range: { min: parseFloat(value_min), max: parseFloat(value_max) },
        description: `${value_min} - ${value_max}`
      };
    case 'not_between':
      // not_between covers everything outside the range
      return { 
        range: { min: -Infinity, max: Infinity }, // Simplified - actual logic is more complex
        description: `not ${value_min} - ${value_max}`
      };
    default:
      return { range: { min: -Infinity, max: Infinity }, description: 'any' };
  }
};

/**
 * Check if two ranges overlap
 */
const rangesOverlap = (range1, range2) => {
  if (!range1 || !range2) return false;
  return range1.min <= range2.max && range2.min <= range1.max;
};

/**
 * Check if branches are exhaustive (cover all possible values for a field)
 * @param {Array} branches - Array of branch objects
 * @param {string} field - Field name to check
 * @param {string} fieldType - Type of the field (number, text, etc.)
 * @returns {Object|null} Warning object if gaps found, null otherwise
 */
export const checkExhaustiveness = (branches, field, fieldType) => {
  if (fieldType !== 'number') return null;
  
  // Collect all ranges for this field
  const ranges = [];
  branches.forEach(branch => {
    branch.conditionGroups?.forEach(group => {
      group.conditions?.forEach(condition => {
        if (condition.field === field) {
          const parsed = parseNumericRange(condition);
          if (parsed.range.min !== -Infinity || parsed.range.max !== Infinity) {
            ranges.push(parsed.range);
          }
        }
      });
    });
  });
  
  if (ranges.length === 0) return null;
  
  // Find gaps between ranges
  const gaps = findGaps(ranges);
  if (gaps.length > 0) {
    return {
      type: 'gap',
      severity: 'info',
      message: `Values in these ranges will fall through to "Else": ${gaps.map(g => 
        g.min === -Infinity ? `< ${g.max}` : 
        g.max === Infinity ? `> ${g.min}` : 
        `${g.min} to ${g.max}`
      ).join(', ')}`,
      suggestion: 'This is fine if intentional. The "Else" branch handles uncovered values.'
    };
  }
  
  return null;
};

/**
 * Find gaps in a list of ranges
 */
const findGaps = (ranges) => {
  if (ranges.length === 0) return [];
  
  // Sort ranges by min value
  const sorted = [...ranges].sort((a, b) => a.min - b.min);
  const gaps = [];
  
  // Check for gap at the beginning (before first range)
  if (sorted[0].min > -Infinity && sorted[0].min > 0) {
    gaps.push({ min: -Infinity, max: sorted[0].min });
  }
  
  // Check for gaps between ranges
  for (let i = 0; i < sorted.length - 1; i++) {
    const currentMax = sorted[i].max;
    const nextMin = sorted[i + 1].min;
    
    if (currentMax < nextMin - 0.0001) {
      gaps.push({ min: currentMax, max: nextMin });
    }
  }
  
  // Check for gap at the end (after last range)
  const lastMax = sorted[sorted.length - 1].max;
  if (lastMax < Infinity) {
    gaps.push({ min: lastMax, max: Infinity });
  }
  
  return gaps;
};

/**
 * Get severity color class for a warning
 */
export const getWarningSeverityClass = (severity) => {
  switch (severity) {
    case 'error':
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: 'text-red-500'
      };
    case 'warning':
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: 'text-yellow-500'
      };
    case 'info':
    default:
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: 'text-blue-500'
      };
  }
};

export default {
  detectBranchConflicts,
  checkExhaustiveness,
  getWarningSeverityClass
};
